import { authConfig } from '@/features/auth/config'
import { formatMembershipPlan } from '@/features/billing/utils/membership-labels'
import type {
  AuthMembership,
  AuthQuotaMetric,
  AuthQuotaSummary,
  AuthSession,
  AuthUser,
  LoginPayload,
  LoginSource,
  RegisterPayload,
} from '@/features/auth/types'

class AuthApiError extends Error {
  constructor(message: string, public readonly status?: number) {
    super(message)
    this.name = 'AuthApiError'
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function readString(payload: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = payload[key]
    if (typeof value === 'string' && value.trim()) {
      return value
    }
  }

  return null
}

function readStringArray(payload: Record<string, unknown>, keys: string[]): string[] {
  for (const key of keys) {
    const value = payload[key]
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === 'string')
    }
  }

  return []
}

function readNumber(payload: Record<string, unknown>, keys: string[]): number | null {
  for (const key of keys) {
    const value = payload[key]
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value
    }
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value)
      if (Number.isFinite(parsed)) {
        return parsed
      }
    }
  }

  return null
}

function resolveAbsoluteUrl(value: string | null | undefined) {
  if (!value) {
    return null
  }

  if (/^(https?:|data:|blob:)/i.test(value)) {
    return value
  }

  if (value.startsWith('/')) {
    const baseUrl = authConfig.mainSiteUrl || authConfig.apiBaseUrl

    if (baseUrl && value.startsWith('/uploads/avatars/')) {
      return `${baseUrl}/api/subsite/user/avatar?path=${encodeURIComponent(value)}`
    }

    return baseUrl ? `${baseUrl}${value}` : value
  }

  return value
}

function normalizeUser(payload: unknown): AuthUser {
  if (!isRecord(payload)) {
    throw new AuthApiError('认证接口未返回有效的用户信息')
  }

  const id = payload.id ?? payload.userId ?? payload.uid ?? payload.email
  const name = payload.name ?? payload.nickname ?? payload.username ?? payload.email
  const email = payload.email ?? payload.mail ?? payload.username

  if (!id || !name || !email) {
    throw new AuthApiError('认证接口返回的用户信息不完整')
  }

  return {
    id: Number(id),
    name: String(name),
    email: String(email),
    avatarUrl: resolveAbsoluteUrl(
      typeof payload.avatarUrl === 'string'
        ? payload.avatarUrl
        : typeof payload.avatar === 'string'
          ? payload.avatar
          : null,
    ),
  }
}

function normalizeQuotaSummary(payload: unknown): AuthQuotaSummary | null {
  if (!isRecord(payload)) {
    return null
  }

  const raw: Partial<Record<AuthQuotaMetric, number>> = {}
  const assignMetric = (metric: AuthQuotaMetric, keys: string[]) => {
    const value = readNumber(payload, keys)
    if (value !== null) {
      raw[metric] = value
    }
    return value
  }

  const aiProcessingSecondsRemaining = assignMetric('ai_processing_seconds', [
    'aiProcessingSecondsRemaining',
    'ai_processing_seconds_remaining',
    'ai_processing_seconds',
  ])

  if (aiProcessingSecondsRemaining === null) {
    return null
  }

  return {
    aiProcessingSecondsRemaining,
    raw,
  }
}

function normalizeMembership(payload: unknown): AuthMembership | null {
  if (!isRecord(payload)) return null

  const planCode = readString(payload, ['planCode', 'plan_code', 'appPlanCode', 'appPlan', 'plan'])
  const rawPlanName = readString(payload, ['planName', 'plan_name', 'appPlanName', 'plan'])
  const quotas =
    normalizeQuotaSummary(payload.quotas) ??
    normalizeQuotaSummary(payload.quotaSummary) ??
    normalizeQuotaSummary(payload)
  const modules = readStringArray(payload, ['modules'])
  const appFeatureFlags = readStringArray(payload, ['appFeatureFlags', 'app_feature_flags'])
  const aiEntitlements = readStringArray(payload, ['aiEntitlements', 'ai_entitlements'])
  const automationEntitlements = readStringArray(payload, [
    'automationEntitlements',
    'automation_entitlements',
  ])

  return {
    productCode: readString(payload, ['product', 'productCode', 'product_code']),
    planCode,
    planName: formatMembershipPlan(rawPlanName, planCode),
    status: readString(payload, ['appSubscriptionStatus', 'status', 'subscriptionStatus']),
    expiresAt: readString(payload, ['expiresAt', 'expireAt', 'appSubscriptionExpiresAt']),
    modules,
    appFeatureFlags,
    aiEntitlements,
    automationEntitlements,
    quotas,
  }
}

function unwrapEnvelope(payload: unknown): Record<string, unknown> {
  if (!isRecord(payload)) {
    throw new AuthApiError('认证接口返回格式不正确')
  }

  const data = isRecord(payload.data) ? payload.data : payload
  return isRecord(data.session) ? data.session : data
}

function normalizeSession(payload: unknown, loginSource: LoginSource): AuthSession {
  const envelope = unwrapEnvelope(payload)
  const membership = normalizeMembership({
    planCode: envelope.planCode,
    planName: envelope.planName,
    status: envelope.subscriptionStatus,
    expiresAt: envelope.expiresAt,
    modules: envelope.modules,
    ...(isRecord(envelope.membership) ? envelope.membership : {}),
  })

  return {
    loginSource,
    user: normalizeUser(
      envelope.user ??
        envelope.profile ??
        envelope.account ?? {
          userId: envelope.userId,
          email: envelope.email,
          nickname: envelope.nickname,
          avatarUrl: envelope.avatarUrl,
        },
    ),
    membership: membership ?? {
      planCode: 'free',
      planName: '基础版',
      status: 'none',
      expiresAt: null,
      modules: [],
    },
  }
}

async function requestJson<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
  const response = await fetch(input, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init?.headers ?? {}),
    },
  })

  const payload = (await response.json().catch(() => null)) as T | { message?: string } | null

  if (!response.ok) {
    const message =
      payload && typeof payload === 'object' && payload && 'message' in payload
        ? String(payload.message)
        : '请求认证接口失败'
    throw new AuthApiError(message, response.status)
  }

  if (payload === null) {
    throw new AuthApiError('认证接口未返回 JSON 数据', response.status)
  }

  return payload as T
}

export async function loginWithPassword(payload: LoginPayload): Promise<AuthSession> {
  const response = await requestJson<unknown>(authConfig.loginEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })

  return normalizeSession(response, 'subsite')
}

export async function registerWithPassword(payload: RegisterPayload): Promise<AuthSession> {
  const response = await requestJson<unknown>(authConfig.registerEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email: payload.email,
      password: payload.password,
      nickname: payload.name,
      deviceFingerprint: payload.deviceFingerprint,
    }),
  })

  return normalizeSession(response, 'subsite')
}

export async function exchangeLaunchTicket(ticket: string): Promise<AuthSession> {
  const response = await requestJson<unknown>(authConfig.launchExchangeEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ticket }),
  })

  return normalizeSession(response, 'launch')
}

export async function fetchCurrentSession(): Promise<AuthSession | null> {
  const response = await requestJson<{ authenticated?: boolean; session?: unknown }>(
    authConfig.meEndpoint,
    {
      method: 'GET',
    },
  )

  if (!response.authenticated || !response.session) {
    return null
  }

  return normalizeSession(response.session, 'subsite')
}

export async function fetchMembershipSummary(): Promise<AuthMembership | null> {
  const response = await requestJson<unknown>(authConfig.accessSummaryEndpoint, {
    method: 'GET',
  })

  return normalizeMembership(response)
}

export async function logoutRemote(): Promise<void> {
  await fetch(authConfig.logoutEndpoint, {
    method: 'POST',
    credentials: 'include',
  }).catch(() => undefined)
}

export { AuthApiError }
